import { AppAnalyticsTag } from '../common/Constants';
import Log from '../common/log';
import Constants from '../analytics/constants';
import Services from '../core/nqs/Services';
export default class AppAnalytics {
    constructor(session) {
        this.appAnalyticsStarted = false;
        this.appAnalyticsStopped = false;
        this.session = session;
        this.session.addListenerRequestQueued(this.onRequestQueued.bind(this));
        this.session.addListenerRequestWillSend(this.onRequestWillSend.bind(this));
        this.session.addListenerRequestSent(this.onRequestSent.bind(this));
    }
    destroy() {
        this.session.destroy();
    }
    setOptions(options) {
        this.session.setOptions(options);
    }
    getOptions() {
        return this.session.getOptions();
    }
    newSession(options, dimensions = {}) {
        if (this.session.isUsingLegacy())
            return;
        this._logFireNewSessionListener(options !== null && options !== void 0 ? options : {}, dimensions);
        // If is simple new session, is not necessary to close the opened session (because has the same behaviour than begin method)
        if (!this.session.isSimpleNewSession()) {
            if (this.appAnalyticsStarted || this.appAnalyticsStopped) {
                // In-memory active or recently stopped session: send /sessionStop.
                // On its successful response RequestProcessor calls
                // ExpirationRecovery.closeSession(), which refreshes the FastData
                // token so the upcoming /sessionStart carries a fresh sessionRoot.
                this.endSession();
            }
            else if (this.session.hasPersistedSession()) {
                // Fresh plugin instance that inherited a previous session from
                // localStorage (e.g. page reload, HbbTV app death + relaunch within
                // `sessionExpire`). No /sessionStop can be sent for it, but the
                // persisted sessionRoot MUST be invalidated client-side; otherwise
                // backend (NQS) finds the same sessionId in SessionsLocalCache and
                // treats the incoming /sessionStart as nav + beat on the existing
                // session instead of creating a new one. destroySession() (public
                // since 7.3.30, PGN-4830) drops the queue, calls
                // ExpirationRecovery.closeSession() and refreshes the token.
                this.session.destroySession();
            }
        }
        if (options) {
            this.setOptions(options);
        }
        this.begin(dimensions);
    }
    begin(dimensions = {}) {
        if (this.session.isUsingLegacy()) {
            return;
        }
        this._logBeginEvent(dimensions);
        if (this.appAnalyticsStopped) {
            this.appAnalyticsStopped = false;
        }
        if (!this.appAnalyticsStarted) {
            this.appAnalyticsStarted = true;
            if (!this.session.expiredSession()) {
                this.session.start(undefined, dimensions, undefined, undefined, true);
            }
        }
    }
    endSession(params = {}) {
        if (this.session.isUsingLegacy())
            return;
        if (this.appAnalyticsStarted) {
            this.appAnalyticsStopped = true;
            this.appAnalyticsStarted = false;
            this.session.stop(params, undefined, undefined, true);
        }
    }
    /**
     * @deprecated The new method is begin(dimensions)
     */
    fireSessionStart(dimensions = {}) {
        this.begin(dimensions);
    }
    /**
     * @deprecated The new method is endSession(params)
     */
    fireSessionStop(params = {}) {
        this.endSession(params);
    }
    fireEvent(eventName = '', dimensions = {}, values = {}, topLevelDimensions = {}, hasEndDatetime = false) {
        this.session.sendEvent(eventName, dimensions, values, topLevelDimensions, hasEndDatetime);
    }
    fireEventEnd(eventName = '') {
        this.session.sendEventEnd(eventName);
    }
    fireError(code, msg, errorType = '', metadata = '', duration = 0, dimensions = {}, values = {}) {
        if (this.session.isUsingLegacy())
            return;
        if (this.session.isActive()) {
            this.session.fireError(code, msg, errorType, metadata, duration, dimensions, values);
        }
    }
    /**
     * Tracks an application error or crash and forwards it to the App Analytics session.
     * The helper wraps {@link fireError} and builds the expected dimensions payload internally.
     *
     * @param errorCode - Unique identifier that categorises the error.
     * @param errorMessage - Human readable description for the error.
     * @param errorType - Optional error classification (for example `crash`, `exception`).
     * @param errorSource - Optional component or module where the error originated.
     * @param metadata - Optional JSON string with additional diagnostic information.
     * @param page - Optional page or section where the issue was detected.
     * @param fatal - Optional flag indicating whether the error is fatal.
     * @param duration - Optional duration in milliseconds associated with the error context.
     * @example
     * npawPlugin.appAnalytics.trackAppError(
     *   'AUTH_001',
     *   'Login request timed out',
     *   'exception',
     *   'AuthService',
     *   JSON.stringify({ attempt: 2 }),
     *   'login',
     *   false,
     *   1500
     * );
     */
    trackAppError(errorCode, errorMessage, errorType, errorSource, metadata, page, fatal, duration) {
        if (!this.ensureSessionStarted(`track app error with code '${errorCode}'`)) {
            return;
        }
        const dimensions = this.buildDimensionMap({
            errorType,
            errorSource,
            page,
            fatal
        });
        this.session.fireError(errorCode, errorMessage, errorType !== null && errorType !== void 0 ? errorType : '', metadata !== null && metadata !== void 0 ? metadata : '', duration !== null && duration !== void 0 ? duration : 0, dimensions, {});
    }
    /**
     * Tracks a log entry for debugging or monitoring purposes.
     * The helper uses {@link fireEvent} with the predefined `logEvent` name.
     *
     * @param logLevel - Severity level (for example `debug`, `info`, `warning`).
     * @param logCategory - Logical grouping or module that produced the log.
     * @param errorSource - Optional component that originated the log.
     * @param page - Optional page or section where the log was generated.
     * @param metadata - Optional JSON string with additional details.
     * @example
     * npawPlugin.appAnalytics.trackLogEvent('info', 'player', 'PlaybackEngine', 'player', JSON.stringify({ state: 'buffering' }));
     */
    trackLogEvent(logLevel, logCategory, errorSource, page, metadata) {
        if (!this.ensureSessionStarted(`track log event with level '${logLevel}' and category '${logCategory}'`)) {
            return;
        }
        this.processSessionEvent('logEvent', {
            logLevel,
            logCategory,
            errorSource,
            page,
            metadata
        });
    }
    /**
     * Tracks an Application Not Responding (ANR) incident via `anrEvent`.
     *
     * @param anrType - Identifies the ANR type (for example `main_thread`, `background`).
     * @param page - Optional page or section where the ANR occurred.
     * @param duration - Optional ANR duration in milliseconds.
     * @example
     * npawPlugin.appAnalytics.trackANREvent('main_thread', 'player', 2100);
     */
    trackANREvent(anrType, page, duration) {
        if (!this.ensureSessionStarted(`track ANR event of type '${anrType}'`)) {
            return;
        }
        this.processSessionEvent('anrEvent', {
            anrType,
            page
        }, {
            duration
        });
    }
    /**
     * Tracks the outcome of a login attempt with the `appLoginResult` event name.
     *
     * @param authType - Authentication method (for example `email`, `social`).
     * @param authStatus - Result of the attempt (`success`, `failed`, `cancelled`).
     * @param retryCounter - Optional number of retries executed before this attempt.
     * @param page - Optional page or screen where the login was performed.
     * @param profileId - Optional profile identifier associated with the user.
     * @param username - Optional username used during the login attempt.
     * @example
     * npawPlugin.appAnalytics.trackLoginResult('email', 'success', 1, 'login', 'profile-42', 'user@example.com');
     */
    trackLoginResult(authType, authStatus, retryCounter, page, profileId, username) {
        if (!this.ensureSessionStarted(`track login result with status '${authStatus}'`)) {
            return;
        }
        this.processSessionEvent('appLoginResult', {
            authType,
            authStatus,
            retryCounter,
            page
        }, {}, {
            profileId,
            username
        });
    }
    /**
     * Tracks the outcome of a user registration attempt with the `appSignupResult` event name.
     *
     * @param authType - Registration mechanism (for example `email`, `social`).
     * @param authStatus - Result of the signup (`success`, `failed`, `cancelled`).
     * @param retryCounter - Optional number of retries executed before this attempt.
     * @param page - Optional page or screen where the signup was performed.
     * @param profileId - Optional profile identifier assigned after signup.
     * @param username - Optional username collected during signup.
     * @example
     * npawPlugin.appAnalytics.trackSignupResult('email', 'failed', 3, 'signup', undefined, 'user@example.com');
     */
    trackSignupResult(authType, authStatus, retryCounter, page, profileId, username) {
        if (!this.ensureSessionStarted(`track signup result with status '${authStatus}'`)) {
            return;
        }
        this.processSessionEvent('appSignupResult', {
            authType,
            authStatus,
            retryCounter,
            page
        }, {}, {
            profileId,
            username
        });
    }
    /**
     * Reports application health and resource information through the `appStatusMonitoring` event.
     *
     * @param batteryLevel - Optional textual battery level (`low`, `medium`, `high`).
     * @param availableMemory - Optional textual descriptor of free memory (`low`, `normal`).
     * @param networkType - Optional network type (`wifi`, `cellular`).
     * @param appForegroundBackground - Optional state describing whether the app is in foreground/background.
     * @param diskSpace - Optional textual descriptor of disk availability.
     * @param batteryPercentage - Optional numeric battery percentage.
     * @param memoryMB - Optional numeric available memory (in MB).
     * @param diskMB - Optional numeric available disk space (in MB).
     * @example
     * npawPlugin.appAnalytics.trackAppStatus('medium', 'normal', 'wifi', 'foreground', 'normal', 78, 2048, 10240);
     */
    trackAppStatus(batteryLevel, availableMemory, networkType, appForegroundBackground, diskSpace, batteryPercentage, memoryMB, diskMB) {
        if (!this.ensureSessionStarted('track app status monitoring event')) {
            return;
        }
        this.processSessionEvent('appStatusMonitoring', {
            batteryLevel,
            availableMemory,
            networkType,
            appForegroundBackground,
            diskSpace
        }, {
            batteryPercentage,
            memoryMB,
            diskMB
        });
    }
    /**
     * Tracks API request performance metrics through the `apiResponsePerformance` event.
     *
     * @param apiProvider - Name of the service or provider handling the request.
     * @param apiEndpoint - Endpoint path that was called.
     * @param method - HTTP method used (for example `GET`, `POST`).
     * @param apiResponseCode - Business response code returned by the API.
     * @param httpStatus - HTTP status code.
     * @param page - Optional page or section that triggered the request.
     * @param responseTime - Optional response time in milliseconds.
     * @example
     * npawPlugin.appAnalytics.trackAPIResponse('AuthService', '/v1/login', 'POST', 'OK', 200, 'login', 120);
     */
    trackAPIResponse(apiProvider, apiEndpoint, method, apiResponseCode, httpStatus, page, responseTime) {
        if (!this.ensureSessionStarted(`track API response for endpoint '${apiEndpoint}'`)) {
            return;
        }
        this.processSessionEvent('apiResponsePerformance', {
            apiProvider,
            apiEndpoint,
            method,
            apiResponseCode,
            httpStatus: httpStatus.toString(),
            page
        }, {
            apiResponseTime: responseTime
        });
    }
    /**
     * Tracks Electronic Program Guide (EPG) refresh metrics with the `epgPerformance` event.
     *
     * @param epgProvider - Name of the EPG data provider.
     * @param refreshStatus - Status of the refresh (`success`, `failed`, `partial`).
     * @param page - Optional page or screen where the EPG was accessed.
     * @param refreshDuration - Optional duration of the refresh in milliseconds.
     * @param channelsLoaded - Optional number of channels loaded.
     * @example
     * npawPlugin.appAnalytics.trackEPGPerformance('GridService', 'success', 'guide', 350, 120);
     */
    trackEPGPerformance(epgProvider, refreshStatus, page, refreshDuration, channelsLoaded) {
        if (!this.ensureSessionStarted(`track EPG performance for provider '${epgProvider}'`)) {
            return;
        }
        this.processSessionEvent('epgPerformance', {
            epgProvider,
            refreshStatus,
            page
        }, {
            refreshDuration,
            channelsLoaded
        });
    }
    /**
     * Tracks remote control interactions with the `remoteControlEvent` event name.
     *
     * @param rcuButton - Button pressed on the remote control (`up`, `down`, `select`, etc.).
     * @param actionTaken - Action triggered as a consequence of the button press.
     * @param eventSuccess - Indicates whether the action succeeded (`true` / `false` string).
     * @param page - Optional page or section where the interaction occurred.
     * @example
     * npawPlugin.appAnalytics.trackRemoteControlEvent('select', 'openDetails', 'true', 'player');
     */
    trackRemoteControlEvent(rcuButton, actionTaken, eventSuccess, page) {
        if (!this.ensureSessionStarted(`track remote control event for button '${rcuButton}'`)) {
            return;
        }
        this.processSessionEvent('remoteControlEvent', {
            rcuButton,
            actionTaken,
            eventSuccess,
            page
        });
    }
    fireCrashError(code, msg, metadata = '', duration = 0, dimensions = {}, values = {}) {
        if (this.session.isUsingLegacy())
            return;
        if (this.session.isActive()) {
            this.session.fireError(code, msg, 'Crash', metadata, duration, dimensions, values);
        }
    }
    fireNavigation(dimensions = {}) {
        if (this.session.isUsingLegacy())
            return;
        if (this.session.isActive()) {
            this.appAnalyticsStarted = true;
            this.session.fireNav(dimensions);
        }
    }
    /**
     * @deprecated The new method is fireNavigation(dimensions)
     */
    fireNav(dimensions = {}) {
        this.fireNavigation(dimensions);
    }
    isActive() {
        return this.session.isActive();
    }
    isStarted() {
        return this.appAnalyticsStarted;
    }
    getSessionRoot() {
        if (this.isActive()) {
            return this.session.getSessionRoot();
        }
        else {
            return null;
        }
    }
    getSessionHost() {
        if (this.isActive()) {
            return this.session.getSessionHost();
        }
        else {
            return null;
        }
    }
    _logBeginEvent(dimensions) {
        const params = {
            logs: {
                data: dimensions
            },
            logAction: 'beginSession',
            logType: 'pluginMethod'
        };
        this.session._sendPluginLogs(Constants.WillSendLog.WILL_SEND_LOG_INFINITY_BEGIN, Constants.Service.APP_ANALYTICS_PLUGIN_LOGS, params);
    }
    _logFireNewSessionListener(options, eventParams) {
        const params = {
            logs: {
                data: options,
                params: eventParams
            },
            logAction: 'newSession',
            logType: 'pluginMethod'
        };
        this.session._sendPluginLogs(Constants.WillSendLog.WILL_SEND_LOG_INFINITY_NAV, Constants.Service.APP_ANALYTICS_PLUGIN_LOGS, params);
    }
    // Listeners
    onRequestQueued(sessionRequest) {
        Log.debug(AppAnalyticsTag, 'Request queued', sessionRequest);
    }
    onRequestWillSend(sessionRequest) {
        Log.debug(AppAnalyticsTag, 'Request send', sessionRequest);
    }
    onRequestSent(sessionRequest) {
        Log.debug(AppAnalyticsTag, 'Request sent', sessionRequest);
    }
    ensureSessionStarted(actionDescription) {
        if (!this.isStarted()) {
            Log.error(AppAnalyticsTag, `Cannot ${actionDescription} because AppAnalytics session is not started. Call fireSessionStart() first.`);
            return false;
        }
        return true;
    }
    buildDimensionMap(source = {}) {
        const dimensions = {};
        Object.keys(source).forEach((key) => {
            const value = source[key];
            if (value !== undefined && value !== null) {
                dimensions[key] = String(value);
            }
        });
        return dimensions;
    }
    buildMetricsMap(source = {}) {
        const metrics = {};
        Object.keys(source).forEach((key) => {
            const value = source[key];
            if (value !== undefined && value !== null) {
                const parsed = Number(value);
                if (!Number.isNaN(parsed)) {
                    metrics[key] = parsed;
                }
            }
        });
        return metrics;
    }
    processSessionEvent(eventName, dimensions = {}, metrics = {}, topLevel = {}, hasEndDatetime = false) {
        const dimensionMap = this.buildDimensionMap(dimensions);
        const metricsMap = this.buildMetricsMap(metrics);
        const topLevelMap = this.buildDimensionMap(topLevel);
        this.session.sendEvent(eventName, dimensionMap, metricsMap, topLevelMap, hasEndDatetime);
    }
    // ------------------------------------------------------------------------------------------
    // PUBLIC SESSION EVENT LISTENERS
    // ------------------------------------------------------------------------------------------
    /**
     * Register a callback to be executed when the session successfully starts.
     * The callback fires after the /infinity/session/start request receives a 200 OK response.
     *
     * @param callback - Function to execute when session starts
     */
    onSessionStart(callback) {
        this.session.addListenerRequestSent((request) => {
            if (request.getService() === Services.Service.SESSION_START) {
                callback();
            }
        });
    }
    /**
     * Register a callback to be executed when the session successfully stops.
     * The callback fires after the /infinity/session/stop request receives a 200 OK response.
     *
     * @param callback - Function to execute when session stops
     */
    onSessionStop(callback) {
        this.session.addListenerRequestSent((request) => {
            if (request.getService() === Services.Service.SESSION_STOP) {
                callback();
            }
        });
    }
}
